<template>
  <section class="rounded-2xl bg-white border border-slate-200 shadow-sm p-4 md:p-6 h-fit min-h-fit overflow-visible">
    <div class="flex items-start justify-between gap-4 mb-6">
      <div>
        <h1 class="text-lg md:text-xl font-semibold text-slate-800 mb-1">Settings</h1>
        <p class="text-sm text-slate-500 max-w-md">
          Configure system behavior and payment methods.
        </p>
      </div>
      <div v-if="loading" class="text-sm text-slate-500">Loading...</div>
    </div>

    <div class="space-y-12">
      <div v-if="canSeeSystemSetting || canSeeClearData || canSeeQrGenerate || canSeeCompanyConstants || canSeePasswordReset" class="grid grid-cols-1 lg:grid-cols-2 gap-x-12 gap-y-12 items-start">
        <!-- Section 1: System Behavior -->
        <div v-if="canSeeSystemSetting" class="space-y-6">
          <h2 class="text-base font-bold text-[#0d4169] flex items-center gap-2 h-7">
            <svg class="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"></path><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"></path></svg>
            System Behavior
          </h2>
          
          <div class="space-y-4">
            <div class="flex items-center justify-between p-4 rounded-xl border border-slate-200 bg-slate-50/40 hover:bg-slate-50 transition-colors">
              <div class="flex flex-col">
                <span class="text-sm font-bold text-slate-700">Printable Order?</span>
                <span class="text-xs text-slate-500">Enable or disable printing in POS.</span>
              </div>
              <label class="toggle-switch" :class="!canEditSystemSetting ? 'opacity-50 cursor-not-allowed pointer-events-none' : ''" :title="settings.is_printer ? 'Turn off' : 'Turn on'">
                <input type="checkbox" :checked="settings.is_printer === 1" @change="togglePrinter" :disabled="!canEditSystemSetting">
                <span class="toggle-slider"></span>
              </label>
            </div>

            <div class="flex items-center justify-between p-4 rounded-xl border border-slate-200 bg-slate-50/40 hover:bg-slate-50 transition-colors">
              <div class="flex flex-col">
                <span class="text-sm font-bold text-slate-700">Department Merged</span>
                <span class="text-xs text-slate-500">Merge departments in POS/printing by IP.</span>
              </div>
              <label class="toggle-switch" :class="!canEditSystemSetting ? 'opacity-50 cursor-not-allowed pointer-events-none' : ''" :title="settings.is_department_merged ? 'Turn off' : 'Turn on'">
                <input type="checkbox" :checked="settings.is_department_merged === 1" @change="toggleDepartmentMerged" :disabled="!canEditSystemSetting">
                <span class="toggle-slider"></span>
              </label>
            </div>

            <div class="flex items-center justify-between p-4 rounded-xl border border-slate-200 bg-slate-50/40 hover:bg-slate-50 transition-colors">
              <div class="flex flex-col">
                <span class="text-sm font-bold text-slate-700">SIV only with approval</span>
                <span class="text-xs text-slate-500">Allow creating SIV only from approved requests.</span>
              </div>
              <label class="toggle-switch" :class="!canEditSystemSetting ? 'opacity-50 cursor-not-allowed pointer-events-none' : ''" :title="settings.siv_by_approval ? 'Turn off' : 'Turn on'">
                <input type="checkbox" :checked="settings.siv_by_approval === 1" @change="toggleSivByApproval" :disabled="!canEditSystemSetting">
                <span class="toggle-slider"></span>
              </label>
            </div>

            <div class="flex items-center justify-between p-4 rounded-xl border border-slate-200 bg-slate-50/40 hover:bg-slate-50 transition-colors">
              <div class="flex flex-col">
                <span class="text-sm font-bold text-slate-700">Is Attendance By Device</span>
                <span class="text-xs text-slate-500">Enable or disable attendance recording via device.</span>
              </div>
              <label class="toggle-switch" :class="!canEditSystemSetting ? 'opacity-50 cursor-not-allowed pointer-events-none' : ''" :title="settings.is_attendance_device ? 'Turn off' : 'Turn on'">
                <input type="checkbox" :checked="settings.is_attendance_device === 1" @change="toggleAttendanceDevice" :disabled="!canEditSystemSetting">
                <span class="toggle-slider"></span>
              </label>
            </div>

            <div class="flex items-center justify-between p-4 rounded-xl border border-slate-200 bg-slate-50/40 hover:bg-slate-50 transition-colors">
              <div class="flex flex-col">
                <span class="text-sm font-bold text-slate-700">Is a table number mandatory?</span>
                <span class="text-xs text-slate-500">Require a table number before sending POS orders.</span>
              </div>
              <label class="toggle-switch" :class="!canEditSystemSetting ? 'opacity-50 cursor-not-allowed pointer-events-none' : ''" :title="settings.is_table_mandatory ? 'Turn off' : 'Turn on'">
                <input type="checkbox" :checked="settings.is_table_mandatory === 1" @change="toggleTableMandatory" :disabled="!canEditSystemSetting">
                <span class="toggle-slider"></span>
              </label>
            </div>

            <div class="flex items-center justify-between p-4 rounded-xl border border-slate-200 bg-slate-50/40 hover:bg-slate-50 transition-colors">
              <div class="flex flex-col">
                <span class="text-sm font-bold text-slate-700">Allow Multiple Orders Confirmation</span>
                <span class="text-xs text-slate-500">Allow confirming all orders for one waiter with a single click.</span>
              </div>
              <label class="toggle-switch" :class="!canEditSystemSetting ? 'opacity-50 cursor-not-allowed pointer-events-none' : ''" :title="settings.allow_multiple_order_confirmation ? 'Turn off' : 'Turn on'">
                <input type="checkbox" :checked="settings.allow_multiple_order_confirmation === 1" @change="toggleMultipleOrderConfirmation" :disabled="!canEditSystemSetting">
                <span class="toggle-slider"></span>
              </label>
            </div>

          </div>
          
          <div v-if="error" class="text-sm text-red-600 font-semibold bg-red-50 p-3 rounded-lg border border-red-100 max-w-md">{{ error }}</div>
        </div>

        <!-- Section: Clear Data -->
        <div v-if="canSeeClearData" class="space-y-6 text-left">
          <h2 class="mb-2 text-base font-bold text-[#0d4169] flex items-center gap-2">
            <svg class="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path></svg>
            Clear Order Data
          </h2>
          <span class="text-red-500 pt-2 pl-7">Cleared data cannot be recovered!</span>

          <div class="bg-slate-50/40 border border-slate-200 rounded-xl p-5 shadow-sm space-y-5" :class="!canVoidClearData ? 'opacity-50 pointer-events-none' : ''">
            <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div class="flex flex-col gap-1 text-left">
                <label class="text-[0.78rem] font-semibold text-[#1e293b]">Start Date</label>
                <input
                  v-model="clearDataFilters.startDate"
                  type="date"
                  class="px-2.5 py-1.5 border border-[#cbd5e1] rounded-md outline-none focus:border-[#0d4169] transition-all text-sm"
                />
              </div>
              <div class="flex flex-col gap-1 text-left">
                <label class="text-[0.78rem] font-semibold text-[#1e293b]">End Date</label>
                <input
                  v-model="clearDataFilters.endDate"
                  type="date"
                  class="px-2.5 py-1.5 border border-[#cbd5e1] rounded-md outline-none focus:border-[#0d4169] transition-all text-sm"
                />
              </div>
            </div>

            <div class="pt-2 flex justify-end">
              <!-- :disabled="savingClearData || !canVoidClearData" -->
              <!-- class="px-4 py-1.5 bg-red-500 text-white text-sm font-bold rounded-md shadow-md hover:bg-red-600 transition-all active:scale-95 flex items-center gap-2 disabled:opacity-50" -->
             
              <button
                :disabled="true"
                @click="clearData"
                
              >
                <span v-if="savingClearData" class="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                <!-- <span>{{ savingClearData ? 'Clearing...' : 'Clear permanently' }}</span> -->
              </button>
            </div>
          </div>
        </div>

        <!-- Section: QR Code Generator (separate from Clear Order Data privilege) -->
        <div v-if="canSeeQrGenerate" class="space-y-6 text-left">
          <h2 class="mb-2 text-base font-bold text-[#0d4169] flex items-center gap-2">
            <svg class="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 3h8v8H3V3zm10 0h8v8h-8V3zM3 13h8v8H3v-8zm10 2h2m-2 4h8m-8-8h8"></path>
            </svg>
            QR Code Generator
          </h2>

          <div
            class="bg-slate-50/40 border border-slate-200 rounded-xl p-5 shadow-sm space-y-4"
            :class="!canGenerateQr ? 'opacity-50 pointer-events-none' : ''"
          >
            <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div class="flex flex-col gap-1 text-left">
                <label class="text-[0.78rem] font-semibold text-[#1e293b]">Start Num <span class="text-red-500">*</span></label>
                <input
                  v-model.trim="qrFilters.startNum"
                  type="number"
                  min="1"
                  class="px-2.5 py-1.5 border border-[#cbd5e1] rounded-md outline-none focus:border-[#0d4169] transition-all text-sm"
                />
              </div>
              <div class="flex flex-col gap-1 text-left">
                <label class="text-[0.78rem] font-semibold text-[#1e293b]">End Num <span class="text-slate-400">(Optional)</span></label>
                <input
                  v-model.trim="qrFilters.endNum"
                  type="number"
                  min="1"
                  class="px-2.5 py-1.5 border border-[#cbd5e1] rounded-md outline-none focus:border-[#0d4169] transition-all text-sm"
                />
              </div>
            </div>
            <div class="grid grid-cols-1 gap-4">
              <!-- Printer IP removed: QR PDF is generated and downloaded directly on frontend -->
            </div>
            <div class="pt-1 flex justify-end">
              <button
                @click="generateQrPdf"
                class="px-4 py-1.5 bg-[#0d4169] text-white text-sm font-bold rounded-md shadow-md hover:bg-[#0a3354] transition-all active:scale-95 flex items-center gap-2 disabled:opacity-50"
                :disabled="generatingQrPdf || !canGenerateQr"
              >
                <span v-if="generatingQrPdf" class="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                <span>{{ generatingQrPdf ? 'Generating...' : 'Generate' }}</span>
              </button>
            </div>
          </div>
        </div>

        <!-- Section: Company Constants -->
        <div v-if="canSeeCompanyConstants" class="space-y-6 text-left">
          <h2 class="mb-2 text-base font-bold text-[#0d4169] flex items-center gap-2">
            <svg class="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8c-2.21 0-4 1.79-4 4m8 0a4 4 0 00-4-4m0 0V4m0 16v-4m8-4h-4M8 12H4"></path>
            </svg>
            Company Constants
          </h2>

          <div
            class="bg-slate-50/40 border border-slate-200 rounded-xl p-5 shadow-sm space-y-4"
            :class="!canEditCompanyConstants ? 'opacity-50 pointer-events-none' : ''"
          >
            <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div class="flex flex-col gap-1 text-left">
                <label class="text-[0.78rem] font-semibold text-[#1e293b]">Tax Percentage</label>
                <input
                  v-model.number="companyConstants.tax"
                  type="number"
                  min="0"
                  step="0.01"
                  class="px-2.5 py-1.5 border border-[#cbd5e1] rounded-md outline-none focus:border-[#0d4169] transition-all text-sm"
                />
              </div>
              <div class="flex flex-col gap-1 text-left">
                <label class="text-[0.78rem] font-semibold text-[#1e293b]">Addition Percentage</label>
                <input
                  v-model.number="companyConstants.addition"
                  type="number"
                  min="0"
                  step="0.01"
                  class="px-2.5 py-1.5 border border-[#cbd5e1] rounded-md outline-none focus:border-[#0d4169] transition-all text-sm"
                />
              </div>
            </div>

            <div class="pt-1 flex justify-end">
              <button
                @click="saveCompanyConstants"
                class="px-4 py-1.5 bg-[#0d4169] text-white text-sm font-bold rounded-md shadow-md hover:bg-[#0a3354] transition-all active:scale-95 flex items-center gap-2 disabled:opacity-50"
                :disabled="savingCompanyConstants || !canEditCompanyConstants"
              >
                <span v-if="savingCompanyConstants" class="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                <span>{{ savingCompanyConstants ? 'Saving...' : 'Save' }}</span>
              </button>
            </div>
          </div>
        </div>

        <!-- Section: Password Reset -->
        <div v-if="canSeePasswordReset" class="space-y-6 text-left">
          <h2 class="mb-2 text-base font-bold text-[#0d4169] flex items-center gap-2">
            <svg class="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 11c1.657 0 3-1.567 3-3.5S13.657 4 12 4 9 5.567 9 7.5 10.343 11 12 11zm-7 9a7 7 0 1114 0H5zm11-9h2a2 2 0 012 2v3m-1.5-1.5l3 3m0 0l3-3m-3 3v-7"></path>
            </svg>
            Password Reset
          </h2>

          <div
            class="bg-slate-50/40 border border-slate-200 rounded-xl p-5 shadow-sm space-y-4"
            :class="!canResetPassword ? 'opacity-50 pointer-events-none' : ''"
          >
            <div class="flex flex-col gap-1 text-left">
              <label class="text-[0.78rem] font-semibold text-[#1e293b]">Select User <span class="text-red-500">*</span></label>
              <select
                v-model="passwordResetForm.userId"
                class="px-2.5 py-1.5 border border-[#cbd5e1] rounded-md outline-none focus:border-[#0d4169] transition-all text-sm bg-white"
              >
                <option value="">Choose user</option>
                <option v-for="user in passwordResetUsers" :key="user.id" :value="String(user.id)">
                  {{ user.full_name || user.username }} ({{ user.username }})
                </option>
              </select>
            </div>

            <div class="pt-1 flex justify-end">
              <button
                @click="resetSelectedUserPassword"
                class="px-4 py-1.5 bg-[#0d4169] text-white text-sm font-bold rounded-md shadow-md hover:bg-[#0a3354] transition-all active:scale-95 flex items-center gap-2 disabled:opacity-50"
                :disabled="resettingPassword || !passwordResetForm.userId || !canResetPassword"
              >
                <span v-if="resettingPassword" class="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                <span>{{ resettingPassword ? 'Resetting...' : 'Reset Password' }}</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <hr class="border-slate-100">

      <!-- Section 2: 2-Column Row for Payment & Delivery -->
      <div v-if="canSeePaymentMethods || canSeeDeliveryCompany" class="grid grid-cols-1 lg:grid-cols-2 gap-x-12 gap-y-12 items-start">
        
        <!-- Column A: Payment Methods -->
        <div v-if="canSeePaymentMethods" class="space-y-6">
          <h2 class="text-base font-bold text-[#0d4169] flex items-center gap-2 h-7">
            <svg class="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"></path></svg>
            {{ editingPM ? 'Edit' : 'Payment' }} Methods
          </h2>

          <!-- Registration Form -->
          <div v-if="canAddPaymentMethods || (editingPM && canEditPaymentMethods)" class="bg-slate-50/40 border border-slate-200 rounded-xl p-3 shadow-sm border-t-2" :class="editingPM ? 'border-sky-500 bg-sky-50/30' : 'border-slate-200'">
            <div class="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
              <div class="flex flex-col gap-1">
                <label class="text-[0.75rem] font-bold text-slate-700 ml-1">Method Name</label>
                <input 
                  v-model="newPM.name"
                  @keyup.enter="handleAction"
                  type="text" 
                  placeholder="e.g. Cash, Visa"
                  class="px-3 py-1.5 border border-slate-200 rounded-lg outline-none focus:border-[#0d4169] transition-all text-sm bg-white font-medium"
                >
              </div>
              <div class="flex flex-col gap-1">
                <label class="text-[0.75rem] font-bold text-slate-700 ml-1">Logo (Optional)</label>
                <label 
                  for="pm-logo-upload" 
                  class="flex items-center gap-3 bg-white border border-slate-200 rounded-lg p-1.5 pr-2 cursor-pointer hover:border-[#0d4169] transition-all group min-h-[38px]"
                >
                  <div v-if="newPM.logo" class="relative">
                    <img :src="resolveCompanyLogoUrl(newPM.logo)" class="w-8 h-8 object-contain rounded bg-slate-50 border border-slate-100">
                    <button @click.prevent="newPM.logo = ''" class="absolute -top-1.5 -right-1.5 bg-red-500 text-white rounded-full w-3.5 h-3.5 flex items-center justify-center text-[9px] shadow-sm hover:bg-red-600 transition-colors z-10">✕</button>
                  </div>
                  <div v-else class="w-8 h-8 rounded bg-slate-50 border border-dashed border-slate-300 flex items-center justify-center text-slate-300 group-hover:text-[#0d4169] group-hover:border-[#0d4169] transition-all">
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"></path></svg>
                  </div>
                  <div class="flex-1 leading-none">
                    <p class="text-[10px] font-bold text-[#0d4169] group-hover:underline">
                      <span v-if="uploadingLogo">Uploading...</span>
                      <span v-else>Choose Image</span>
                    </p>
                    <p class="text-[8px] text-slate-400 mt-0.5">PNG, JPG, BMP</p>
                  </div>
                  <input id="pm-logo-upload" type="file" class="hidden" accept="image/*" @change="handleLogoUpload">
                </label>
              </div>
            </div>
            <div class="flex justify-center gap-3 pt-1">
              <button 
                v-if="editingPM"
                @click="cancelEdit"
                class="px-5 py-1.5 bg-slate-200 text-slate-600 text-[11px] font-bold rounded-lg hover:bg-slate-300 transition-all active:scale-[0.98]"
              >
                Cancel
              </button>
              <button 
                @click="handleAction"
                :disabled="!newPM.name || savingPM || uploadingLogo"
                class="px-8 py-1.5 bg-[#0d4169] text-white text-[11px] font-bold rounded-lg hover:bg-[#0a3354] shadow-sm transition-all disabled:opacity-50 disabled:scale-100 active:scale-[0.98]"
                :class="editingPM ? 'bg-sky-600 hover:bg-sky-700' : ''"
              >
                <span v-if="savingPM">{{ editingPM ? 'Updating...' : 'Adding...' }}</span>
                <span v-else>{{ editingPM ? 'Update Payment Method' : 'Register Payment Method' }}</span>
              </button>
            </div>
          </div>

          <!-- Payment Methods List -->
          <div class="overflow-hidden border border-slate-200 rounded-xl relative">
            <div v-if="loadingPMs" class="absolute inset-0 bg-white/60 backdrop-blur-[1px] z-10 flex items-center justify-center">
              <span class="w-6 h-6 border-2 border-[#0d4169] border-t-transparent rounded-full animate-spin"></span>
            </div>
            <table class="w-full text-left text-xs">
              <thead>
                <tr class="bg-slate-100 text-slate-600 font-bold">
                  <th class="px-4 py-3 w-12 text-center">No.</th>
                  <th class="px-4 py-3">Name</th>
                  <th class="px-4 py-3 w-16 text-center">Active</th>
                  <th class="px-4 py-3 w-16 text-center">Action</th>
                </tr>
              </thead>
              <tbody class="divide-y divide-slate-100 bg-white">
                <tr v-for="(pm, idx) in paymentMethods" :key="pm.id || idx" class="hover:bg-slate-50/80 transition-colors" :class="editingPM?.id === pm.id ? 'bg-sky-50' : ''">
                  <td class="px-4 py-3 text-center text-slate-400 font-medium">{{ idx + 1 }}</td>
                  <td class="px-4 py-3">
                    <div class="flex items-center gap-3">
                      <div v-if="pm.logo" class="w-8 h-8 rounded-md bg-slate-50 flex items-center justify-center overflow-hidden border border-slate-100 shadow-sm">
                        <img :src="resolveCompanyLogoUrl(pm.logo)" class="w-full h-full object-contain p-0.5">
                      </div>
                      <div v-else class="w-8 h-8 rounded-md bg-slate-100 flex items-center justify-center text-[10px] text-slate-400 font-bold uppercase border border-slate-200">
                        {{ pm.name.substring(0, 2) }}
                      </div>
                      <span class="font-semibold text-slate-700">{{ pm.name }}</span>
                    </div>
                  </td>
                  <td class="px-4 py-3 text-center text-[0]">
                    <label class="toggle-switch scale-75 inline-block origin-center align-middle" :class="!canEditPaymentMethods ? 'opacity-50 cursor-not-allowed pointer-events-none' : ''">
                      <input type="checkbox" :checked="pm.isActive === 1" @change="togglePM(pm)" :disabled="!canEditPaymentMethods">
                      <span class="toggle-slider"></span>
                    </label>
                  </td>
                  <td class="px-4 py-3 text-center">
                    <button 
                      v-if="canEditPaymentMethods"
                      @click="editPM(pm)" 
                      class="text-sky-600 font-bold hover:underline transition-all" 
                      title="Edit"
                    >
                      Edit
                    </button>
                    <span v-else class="text-slate-300 font-bold cursor-not-allowed">---</span>
                  </td>
                </tr>
                <tr v-if="paymentMethods.length === 0 && !loadingPMs">
                  <td colspan="4" class="px-4 py-10 text-center text-slate-400 italic bg-white">No payment methods found.</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- Column B: Delivery Companies -->
        <div v-if="canSeeDeliveryCompany" class="space-y-6">
          <h2 class="text-base font-bold text-[#0d4169] flex items-center gap-2 h-7">
            <svg class="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"></path></svg>
            {{ editingDC ? 'Edit' : 'Delivery' }} Companies
          </h2>

          <!-- Registration Form -->
          <div v-if="canAddDeliveryCompany || (editingDC && canEditDeliveryCompany)" class="bg-slate-50/40 border border-slate-200 rounded-xl p-3 shadow-sm border-t-2" :class="editingDC ? 'border-sky-500 bg-sky-50/30' : 'border-slate-200'">
            <div class="flex flex-col gap-1 mb-3">
              <label class="text-[0.75rem] font-bold text-slate-700 ml-1">Company Name</label>
              <input 
                v-model="newDC.name"
                @keyup.enter="handleDCAction"
                type="text" 
                placeholder="e.g. Beu, Feres"
                class="px-3 py-1.5 border border-slate-200 rounded-lg outline-none focus:border-[#0d4169] transition-all text-sm bg-white font-medium"
              >
            </div>
            <div class="flex justify-center gap-3 pt-1">
              <button 
                v-if="editingDC"
                @click="cancelDCEdit"
                class="px-5 py-1.5 bg-slate-200 text-slate-600 text-[11px] font-bold rounded-lg hover:bg-slate-300 transition-all active:scale-[0.98]"
              >
                Cancel
              </button>
              <button 
                @click="handleDCAction"
                :disabled="!newDC.name || savingDC"
                class="px-8 py-1.5 bg-[#0d4169] text-white text-[11px] font-bold rounded-lg hover:bg-[#0a3354] shadow-sm transition-all disabled:opacity-50 disabled:scale-100 active:scale-[0.98]"
                :class="editingDC ? 'bg-sky-600 hover:bg-sky-700' : ''"
              >
                <span v-if="savingDC">{{ editingDC ? 'Updating...' : 'Adding...' }}</span>
                <span v-else>{{ editingDC ? 'Update Delivery Company' : 'Register Delivery Company' }}</span>
              </button>
            </div>
          </div>

          <!-- delivery companies List -->
          <div class="overflow-hidden border border-slate-200 rounded-xl relative">
            <div v-if="loadingDCs" class="absolute inset-0 bg-white/60 backdrop-blur-[1px] z-10 flex items-center justify-center">
              <span class="w-6 h-6 border-2 border-[#0d4169] border-t-transparent rounded-full animate-spin"></span>
            </div>
            <table class="w-full text-left text-xs">
              <thead>
                <tr class="bg-slate-100 text-slate-600 font-bold">
                  <th class="px-4 py-3 w-12 text-center">No.</th>
                  <th class="px-4 py-3">Name</th>
                  <th class="px-4 py-3 w-16 text-center">Active</th>
                  <th class="px-4 py-3 w-16 text-center">Action</th>
                </tr>
              </thead>
              <tbody class="divide-y divide-slate-100 bg-white">
                <tr v-for="(dc, idx) in deliveryCompanies" :key="dc.id || idx" class="hover:bg-slate-50/80 transition-colors" :class="editingDC?.id === dc.id ? 'bg-sky-50' : ''">
                  <td class="px-4 py-3 text-center text-slate-400 font-medium">{{ idx + 1 }}</td>
                  <td class="px-4 py-3 font-semibold text-slate-700">{{ dc.name }}</td>
                  <td class="px-4 py-3 text-center text-[0]">
                    <label class="toggle-switch scale-75 inline-block origin-center align-middle" :class="!canEditDeliveryCompany ? 'opacity-50 cursor-not-allowed pointer-events-none' : ''">
                      <input type="checkbox" :checked="dc.isActive === 1" @change="toggleDC(dc)" :disabled="!canEditDeliveryCompany">
                      <span class="toggle-slider"></span>
                    </label>
                  </td>
                  <td class="px-4 py-3 text-center text-slate-500">
                    <button 
                      v-if="canEditDeliveryCompany"
                      @click="editDC(dc)" 
                      class="text-sky-600 font-bold hover:underline transition-all" 
                      title="Edit"
                    >
                      Edit
                    </button>
                    <span v-else class="text-slate-300 font-bold cursor-not-allowed">---</span>
                  </td>
                </tr>
                <tr v-if="deliveryCompanies.length === 0 && !loadingDCs">
                  <td colspan="4" class="px-4 py-10 text-center text-slate-400 italic bg-white">No delivery companies found.</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>

    <!-- Toast Notification -->
    <Transition name="toast">
      <div v-if="toast.show" class="fixed top-4 right-4 z-[1000] max-w-sm no-print">
        <div 
          :class="[
            'px-6 py-3 rounded-lg shadow-2xl text-white flex items-center gap-3 min-w-[300px]',
            toast.type === 'success' ? 'bg-green-600' : 'bg-red-600'
          ]"
        >
          <span v-if="toast.type == 'success'">✅</span>
          <span v-else>❌</span>
          <span class="font-medium">{{ toast.message }}</span>
        </div>
      </div>
    </Transition>
  </section>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue'
import store from '../store'
import { apiFetch, API_BASE_URL, normalizeUploadValue, resolveCompanyLogoUrl } from '../utils/api'
import { jsPDF } from 'jspdf'
import QRCode from 'qrcode'

const currentUser = computed(() => store.getters.currentUser)
const companyId = computed(() => currentUser.value?.companyId || currentUser.value?.company_id || currentUser.value?.company?.id || null)
const branchId = computed(() => currentUser.value?.branchId || currentUser.value?.branch_id || currentUser.value?.branch?.id || null)

// Computed privileges logic
const userPrivileges = computed(() => {
  if (!currentUser.value?.privilege) return {}
  return typeof currentUser.value.privilege === 'string' 
    ? JSON.parse(currentUser.value.privilege) 
    : currentUser.value.privilege
})

const hasAllPrivilege = computed(() => userPrivileges.value?.All === true || userPrivileges.value?.all === true)

// Helpers for specific sections
const getPrivilegeEntry = (key) => {
  const priv = userPrivileges.value || {}
  if (!key) return null
  if (priv?.[key]) return priv[key]

  // Fallback: match keys case-insensitively + trimmed (handles "QR Generate" vs "QR Generate " etc.)
  const normalizedNeedle = String(key).trim().toLowerCase()
  const foundKey = Object.keys(priv).find(k => String(k).trim().toLowerCase() === normalizedNeedle)
  return foundKey ? priv[foundKey] : null
}

const getActionFlag = (entry, action) => {
  if (!entry || typeof entry !== 'object') return false
  if (!action) return false
  if (entry[action] !== undefined) return !!entry[action]

  const a = String(action)
  const candidates = [
    a.toLowerCase(),
    a.toUpperCase(),
    a.charAt(0).toUpperCase() + a.slice(1).toLowerCase(),
  ]
  for (const k of candidates) {
    if (entry[k] !== undefined) return !!entry[k]
  }
  return false
}

const canAccessSection = (key) => {
  if (hasAllPrivilege.value) return true
  const p = getPrivilegeEntry(key)
  if (!p) return false
  return !!(
    getActionFlag(p, 'Add') ||
    getActionFlag(p, 'Edit') ||
    getActionFlag(p, 'View') ||
    getActionFlag(p, 'Void') ||
    getActionFlag(p, 'Approve')
  )
}

const canDoAction = (key, action) => {
  if (hasAllPrivilege.value) return true
  const p = getPrivilegeEntry(key)
  return getActionFlag(p, action)
}

// Section visibility and action permissions
const canSeeSystemSetting = computed(() => canAccessSection('System Setting'))
const canEditSystemSetting = computed(() => canDoAction('System Setting', 'Edit'))

const canSeeClearData = computed(() => canAccessSection('Clear Data'))
const canVoidClearData = computed(() => canDoAction('Clear Data', 'Edit') || canDoAction('Clear Data', 'Void'))

const canSeePaymentMethods = computed(() => canAccessSection('Payment Methods'))
const canAddPaymentMethods = computed(() => canDoAction('Payment Methods', 'Add'))
const canEditPaymentMethods = computed(() => canDoAction('Payment Methods', 'Edit'))

const canSeeDeliveryCompany = computed(() => canAccessSection('Delivery Company'))
const canAddDeliveryCompany = computed(() => canDoAction('Delivery Company', 'Add'))
const canEditDeliveryCompany = computed(() => canDoAction('Delivery Company', 'Edit'))

// QR Generator privilege: visible only if All=true or QR Generate.Add=true
const canSeeQrGenerate = computed(() => hasAllPrivilege.value || canDoAction('QR Generate', 'Add'))
const canGenerateQr = computed(() => hasAllPrivilege.value || canDoAction('QR Generate', 'Add'))
const canSeeCompanyConstants = computed(() => canAccessSection('Company Constants'))
const canEditCompanyConstants = computed(() => (
  canDoAction('Company Constants', 'Edit') || canDoAction('Company Constants', 'Add')
))
const canSeePasswordReset = computed(() => canAccessSection('Password Reset'))
const canResetPassword = computed(() => canDoAction('Password Reset', 'Edit'))
const companyLogoUrl = computed(() => (
  currentUser.value?.companyLogo ||
  currentUser.value?.company_logo ||
  currentUser.value?.logo ||
  currentUser.value?.company?.logo ||
  currentUser.value?.company?.companyLogo ||
  ''
))

const loading = ref(false)
const loadingPMs = ref(false)
const savingPM = ref(false)
const savingDC = ref(false)
const saving = ref(false) // for general settings
const uploadingLogo = ref(false)
const error = ref('')

const toast = ref({ show: false, message: '', type: 'success' })

function showToast(message, type = 'success') {
  toast.value = { show: true, message, type }
  setTimeout(() => {
    toast.value.show = false
  }, 3000)
}

function getImageFormatFromDataUrl(dataUrl) {
  if (!dataUrl || typeof dataUrl !== 'string') return 'PNG'
  if (dataUrl.startsWith('data:image/jpeg') || dataUrl.startsWith('data:image/jpg')) return 'JPEG'
  if (dataUrl.startsWith('data:image/webp')) return 'WEBP'
  return 'PNG'
}

async function imageUrlToDataUrl(url) {
  if (!url) return { dataUrl: null, format: 'PNG' }
  try {
    const res = await fetch(url)
    if (!res.ok) return { dataUrl: null, format: 'PNG' }
    const blob = await res.blob()
    const dataUrl = await new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.onloadend = () => resolve(reader.result)
      reader.onerror = reject
      reader.readAsDataURL(blob)
    })
    return {
      dataUrl,
      format: getImageFormatFromDataUrl(dataUrl)
    }
  } catch {
    return { dataUrl: null, format: 'PNG' }
  }
}

const editingPM = ref(null)

const settings = ref({
  id: null,
  is_printer: 1,
  is_department_merged: 0,
  siv_by_approval: 0,
  is_attendance_device: 1,
  is_table_mandatory: 1,
  allow_multiple_order_confirmation: 0,
})

// Payment Methods Logic
const paymentMethods = ref([])
const newPM = ref({ name: '', logo: '', isActive: 1 })

// Delivery Companies Logic
const deliveryCompanies = ref([])
const newDC = ref({ name: '', isActive: 1 })
const editingDC = ref(null)
const loadingDCs = ref(false)

const clearDataFilters = ref({
  startDate: '',
  endDate: ''
})

const qrFilters = ref({
  startNum: '',
  endNum: ''
})
const generatingQrPdf = ref(false)
const passwordResetUsers = ref([])
const passwordResetForm = ref({
  userId: ''
})
const resettingPassword = ref(false)
const savingCompanyConstants = ref(false)
const companyConstants = ref({
  id: null,
  companyId: null,
  tax: 15,
  addition: 10,
})

async function loadPMs() {
  loadingPMs.value = true
  try {
    const res = await apiFetch('/settings/payment-methods')
    if (res.ok) {
      paymentMethods.value = (await res.json()).map((pm) => ({
        ...pm,
        logo: normalizeUploadValue(pm.logo),
      }))
    }
  } catch (err) {
    console.error('Error fetching payment methods:', err)
  } finally {
    loadingPMs.value = false
  }
}

async function loadDCs() {
  loadingDCs.value = true
  try {
    const res = await apiFetch('/settings/delivery-companies')
    if (res.ok) {
      deliveryCompanies.value = await res.json()
    }
  } catch (err) {
    console.error('Error fetching delivery companies:', err)
  } finally {
    loadingDCs.value = false
  }
}

async function handleLogoUpload(event) {
  const file = event.target.files[0]
  if (!file) return

  if (file.size > 2 * 1024 * 1024) {
    alert('File is too large. Max 2MB.')
    return
  }

  uploadingLogo.value = true
  try {
    const formData = new FormData()
    formData.append('logo', file)

    const res = await apiFetch('/upload', {
      method: 'POST',
      body: formData
    })

    if (res.ok) {
      const data = await res.json()
      newPM.value.logo = normalizeUploadValue(data.url)
    } else {
      throw new Error('Upload failed')
    }
  } catch (err) {
    console.error('Error uploading logo:', err)
    alert('Failed to upload logo.')
  } finally {
    uploadingLogo.value = false
    // Clear the input value so the same file can be selected again if needed
    event.target.value = ''
  }
}

async function handleAction() {
  if (editingPM.value) {
    await updatePM()
  } else {
    await addPM()
  }
}

async function handleDCAction() {
  if (editingDC.value) {
    await updateDC()
  } else {
    await addDC()
  }
}

async function addPM() {
  if (!newPM.value.name.trim() || savingPM.value) return
  savingPM.value = true
  try {
    const res = await apiFetch('/settings/payment-methods', {
      method: 'POST',
      body: JSON.stringify({
        ...newPM.value,
        logo: normalizeUploadValue(newPM.value.logo) || '',
      })
    })
    if (res.ok) {
      const created = await res.json()
      paymentMethods.value.push({
        ...created,
        logo: normalizeUploadValue(created.logo),
      })
      newPM.value = { name: '', logo: '', isActive: 1 }
      showToast('Payment method added successfully')
    } else {
      showToast('Failed to add payment method', 'error')
    }
  } catch (err) {
    console.error('Error adding payment method:', err)
    showToast('Failed to add payment method', 'error')
  } finally {
    savingPM.value = false
  }
}

async function addDC() {
  if (!newDC.value.name.trim() || savingDC.value) return
  savingDC.value = true
  try {
    const res = await apiFetch('/settings/delivery-companies', {
      method: 'POST',
      body: JSON.stringify(newDC.value)
    })
    if (res.ok) {
      const created = await res.json()
      deliveryCompanies.value.push(created)
      newDC.value = { name: '', isActive: 1 }
      showToast('Delivery company added successfully')
    } else {
      showToast('Failed to add delivery company', 'error')
    }
  } catch (err) {
    console.error('Error adding delivery company:', err)
    showToast('Failed to add delivery company', 'error')
  } finally {
    savingDC.value = false
  }
}

async function updatePM() {
  if (!newPM.value.name.trim() || savingPM.value || !editingPM.value) return
  savingPM.value = true
  try {
    const res = await apiFetch(`/settings/payment-methods/${editingPM.value.id}`, {
      method: 'PUT',
      body: JSON.stringify({
        ...newPM.value,
        logo: normalizeUploadValue(newPM.value.logo) || '',
      })
    })
    if (res.ok) {
      const updated = await res.json()
      const index = paymentMethods.value.findIndex(m => m.id === updated.id)
      if (index !== -1) {
        paymentMethods.value[index] = {
          ...updated,
          logo: normalizeUploadValue(updated.logo),
        }
      }
      cancelEdit()
      showToast('Payment method updated successfully')
    } else {
      showToast('Failed to update payment method', 'error')
    }
  } catch (err) {
    console.error('Error updating payment method:', err)
    showToast('Failed to update payment method', 'error')
  } finally {
    savingPM.value = false
  }
}

async function updateDC() {
  if (!newDC.value.name.trim() || savingDC.value || !editingDC.value) return
  savingDC.value = true
  try {
    const res = await apiFetch(`/settings/delivery-companies/${editingDC.value.id}`, {
      method: 'PUT',
      body: JSON.stringify(newDC.value)
    })
    if (res.ok) {
      const updated = await res.json()
      const index = deliveryCompanies.value.findIndex(m => m.id === updated.id)
      if (index !== -1) {
        deliveryCompanies.value[index] = updated
      }
      cancelDCEdit()
      showToast('Delivery company updated successfully')
    } else {
      showToast('Failed to update delivery company', 'error')
    }
  } catch (err) {
    console.error('Error updating delivery company:', err)
    showToast('Failed to update delivery company', 'error')
  } finally {
    savingDC.value = false
  }
}

function editPM(pm) {
  editingPM.value = pm
  newPM.value = { ...pm, logo: normalizeUploadValue(pm.logo) }
}

function editDC(dc) {
  editingDC.value = dc
  newDC.value = { ...dc }
}

function cancelEdit() {
  editingPM.value = null
  newPM.value = { name: '', logo: '', isActive: 1 }
}

function cancelDCEdit() {
  editingDC.value = null
  newDC.value = { name: '', isActive: 1 }
}

async function togglePM(pm) {
  if (saving.value) return
  const nextStatus = pm.isActive === 1 ? 0 : 1
  try {
    const res = await apiFetch(`/settings/payment-methods/${pm.id}`, {
      method: 'PUT',
      body: JSON.stringify({ isActive: nextStatus })
    })
    if (res.ok) {
      pm.isActive = nextStatus
    }
  } catch (err) {
    console.error('Error toggling payment method:', err)
  }
}

async function toggleDC(dc) {
  if (saving.value) return
  const nextStatus = dc.isActive === 1 ? 0 : 1
  try {
    const res = await apiFetch(`/settings/delivery-companies/${dc.id}`, {
      method: 'PUT',
      body: JSON.stringify({ isActive: nextStatus })
    })
    if (res.ok) {
      dc.isActive = nextStatus
    }
  } catch (err) {
    console.error('Error toggling delivery company:', err)
  }
}

const savingClearData = ref(false)

async function clearData() {
  if (!canVoidClearData.value) return
  
  let msg = 'Are you sure you want to clear order-related data? This operation CANNOT be recovered.'
  if (clearDataFilters.value.startDate && clearDataFilters.value.endDate) {
    msg = `Are you sure you want to clear data from ${clearDataFilters.value.startDate} to ${clearDataFilters.value.endDate}? This cannot be recovered.`
  } else if (clearDataFilters.value.startDate || clearDataFilters.value.endDate) {
    msg = `Are you sure you want to clear data for ${clearDataFilters.value.startDate || clearDataFilters.value.endDate}? This cannot be recovered.`
  } else {
    msg = 'Are you sure you want to clear ALL past order data except for today? This cannot be recovered.'
  }

  if (!window.confirm(msg)) return

  savingClearData.value = true
  try {
    const res = await apiFetch('/settings/clear-orders', {
      method: 'POST',
      body: JSON.stringify(clearDataFilters.value)
    })
    
    if (res.ok) {
      const data = await res.json()
      showToast(data.message || 'Data cleared successfully')
      clearDataFilters.value = { startDate: '', endDate: '' }
    } else {
      const err = await res.json()
      showToast(err.message || 'Failed to clear data', 'error')
    }
  } catch (err) {
    console.error('Error clearing data:', err)
    showToast('An error occurred while clearing data', 'error')
  } finally {
    savingClearData.value = false
  }
}

async function generateQrPdf() {
  const hasStart = String(qrFilters.value.startNum || '').trim() !== ''
  const start = hasStart ? Number(qrFilters.value.startNum) : null
  const hasEnd = String(qrFilters.value.endNum || '').trim() !== ''
  const end = hasEnd ? Number(qrFilters.value.endNum) : null

  if (!hasStart && hasEnd) {
    showToast('Start Num is required when End Num is entered', 'error')
    return
  }

  if (hasStart && (!Number.isInteger(start) || start <= 0)) {
    showToast('Start Num is required and must be a positive number', 'error')
    return
  }

  if (hasEnd && (!Number.isInteger(end) || end <= start)) {
    showToast('End Num must be greater than Start Num', 'error')
    return
  }

  const compId = Number(companyId.value)
  if (!Number.isInteger(compId) || compId <= 0) {
    showToast('Company ID is missing in user token', 'error')
    return
  }

  const branchTokenValue = branchId.value
  const branchParam = branchTokenValue === null || branchTokenValue === undefined || String(branchTokenValue).trim() === ''
    ? 'null'
    : String(branchTokenValue)

  generatingQrPdf.value = true

  try {
    // Derive protocol + hostname from API_BASE_URL (no port), then point to QR frontend port.
    // If hostname is localhost, ask backend for LAN IP so QR works on other devices.
    const apiUrl = new URL(API_BASE_URL)
    let host = apiUrl.hostname
    if (host === 'localhost' || host === '127.0.0.1') {
      try {
        const ipRes = await apiFetch('/runtime/ip', { method: 'GET' })
        if (ipRes.ok) {
          const ipData = await ipRes.json().catch(() => ({}))
          if (ipData?.ip) host = String(ipData.ip)
        }
      } catch {
        // fallback to localhost
      }
    }

    const qrOrigin = `${apiUrl.protocol}//${host}`
    const baseUrl = `${qrOrigin}/qr-order/?companyId=${encodeURIComponent(compId)}&branchId=${encodeURIComponent(branchParam)}&tableNumber=`
    const hasTableNumbers = hasStart
    const from = hasTableNumbers ? start : null
    const to = hasTableNumbers ? (hasEnd ? end : start) : null
    const tableNumbers = hasTableNumbers ? [] : [null]
    if (hasTableNumbers) {
      for (let tableNo = from; tableNo <= to; tableNo += 1) {
        tableNumbers.push(tableNo)
      }
    }

    const doc = new jsPDF({ unit: 'mm', format: 'a4', orientation: 'portrait' })
    const cols = 2
    const rows = 2
    const perPage = cols * rows // 4 QR codes/page
    const pageWidth = 210
    const pageHeight = 297
    const pageMarginX = 12
    const pageMarginY = 12
    const gapX = 6
    const gapY = 8
    const cellWidth = (pageWidth - pageMarginX * 2 - gapX) / cols
    const cellHeight = (pageHeight - pageMarginY * 2 - gapY) / rows
    const qrSize = Math.min(cellWidth - 28, cellHeight - 58)
    const { dataUrl: logoDataUrl, format: logoFormat } = await imageUrlToDataUrl(String(companyLogoUrl.value || '').trim())

    for (let i = 0; i < tableNumbers.length; i += 1) {
      if (i > 0 && i % perPage === 0) {
        doc.addPage()
      }

      const indexInPage = i % perPage
      const col = indexInPage % cols
      const row = Math.floor(indexInPage / cols)

      const cellX = pageMarginX + col * (cellWidth + gapX)
      const cellY = pageMarginY + row * (cellHeight + gapY)
      const qrX = cellX + (cellWidth - qrSize) / 2
      const qrY = cellY + 24
      const tableNo = tableNumbers[i]

      const qrValue = `${baseUrl}${tableNo === null ? '' : encodeURIComponent(tableNo)}`
      const qrDataUrl = await QRCode.toDataURL(qrValue, {
        margin: 0,
        width: 700
      })

      doc.setDrawColor(226, 232, 240)
      doc.roundedRect(cellX, cellY, cellWidth, cellHeight, 2, 2)
      doc.setFont('helvetica', 'bold')
      doc.setFontSize(11)
      doc.text(['Scan And View Our Menu', 'Order and Pay'], cellX + cellWidth / 2, cellY + 10, {
        align: 'center',
        lineHeightFactor: 1.15
      })
      doc.addImage(qrDataUrl, 'PNG', qrX, qrY, qrSize, qrSize, undefined, 'FAST')

      if (logoDataUrl) {
        const logoWrapSize = Math.max(10, qrSize * 0.2)
        const logoSize = logoWrapSize * 0.75
        const logoWrapX = qrX + (qrSize - logoWrapSize) / 2
        const logoWrapY = qrY + (qrSize - logoWrapSize) / 2
        const logoX = qrX + (qrSize - logoSize) / 2
        const logoY = qrY + (qrSize - logoSize) / 2
        doc.setFillColor(255, 255, 255)
        doc.roundedRect(logoWrapX, logoWrapY, logoWrapSize, logoWrapSize, 2, 2, 'F')
        doc.addImage(logoDataUrl, logoFormat, logoX, logoY, logoSize, logoSize, undefined, 'FAST')
      }

      doc.setFont('helvetica', 'bold')
      doc.setFontSize(13)
      doc.text(
        tableNo === null ? '' : `Table ${tableNo}`,
        cellX + cellWidth / 2,
        qrY + qrSize + 8,
        { align: 'center' }
      )
    }

    const filename = from === null
      ? 'qr-no-table-number.pdf'
      : from === to ? `table-qr-${from}.pdf` : `table-qr-${from}-${to}.pdf`
    doc.save(filename)
    showToast('QR PDF downloaded successfully')
  } catch (err) {
    console.error('Failed generating QR PDF:', err)
    showToast('Failed to generate QR PDF', 'error')
  } finally {
    generatingQrPdf.value = false
  }
}

function userHasAllPrivilege(user) {
  if (!user?.privilege) return false
  try {
    const p = typeof user.privilege === 'string'
      ? JSON.parse(user.privilege)
      : user.privilege
    return p?.All === true || p?.all === true
  } catch {
    return false
  }
}

function isCompanyLevelContext() {
  const raw = branchId.value
  return raw === null || raw === undefined || String(raw).trim() === '' || String(raw) === 'null' || String(raw) === 'undefined'
}

function userHasBranchAssignment(user) {
  const raw = user?.branchId ?? user?.branch_id ?? user?.branch?.id ?? null
  return raw !== null && raw !== undefined && String(raw).trim() !== '' && String(raw) !== 'null' && String(raw) !== 'undefined'
}

async function loadPasswordResetUsers() {
  if (!canSeePasswordReset.value) return
  try {
    let users = []
    if (isCompanyLevelContext()) {
      if (!companyId.value) return
      const res = await apiFetch(`/users/company/${companyId.value}`)
      if (!res.ok) return
      users = await res.json()
    } else {
      const res = await apiFetch('/settings/password-reset/users')
      if (!res.ok) return
      users = await res.json()
    }

    const eligibleUsers = (Array.isArray(users) ? users : [])
      .filter((user) => !userHasAllPrivilege(user))

    passwordResetUsers.value = isCompanyLevelContext()
      ? eligibleUsers.filter((user) => !userHasBranchAssignment(user))
      : eligibleUsers
  } catch (err) {
    console.error('Error fetching users for password reset:', err)
  }
}

async function resetSelectedUserPassword() {
  if (!canResetPassword.value || !passwordResetForm.value.userId || resettingPassword.value) return
  const confirmed = window.confirm('Are you sure you want to reset the selected user password?')
  if (!confirmed) return

  resettingPassword.value = true
  try {
    const res = await apiFetch('/settings/password-reset', {
      method: 'POST',
      body: JSON.stringify({ userId: Number(passwordResetForm.value.userId) })
    })

    if (res.ok) {
      const data = await res.json()
      showToast(data.message || 'Password reset successfully')
      window.alert(`New password: ${data.password}`)
      passwordResetForm.value.userId = ''
      return
    }

    const errData = await res.json().catch(() => ({}))
    showToast(errData.message || 'Failed to reset password', 'error')
  } catch (err) {
    console.error('Error resetting password:', err)
    showToast('Failed to reset password', 'error')
  } finally {
    resettingPassword.value = false
  }
}

async function loadCompanyConstants() {
  if (!companyId.value || !canSeeCompanyConstants.value) return
  try {
    const res = await apiFetch('/settings/company-constants')
    if (!res.ok) return
    const data = await res.json()
    companyConstants.value = {
      id: data?.id ?? null,
      companyId: data?.companyId ?? null,
      tax: Number(data?.tax ?? 15),
      addition: Number(data?.addition ?? 10),
    }
  } catch (err) {
    console.error('Error loading company constants:', err)
  }
}

async function saveCompanyConstants() {
  if (!canEditCompanyConstants.value || savingCompanyConstants.value) return
  const payload = {
    id: Number(companyConstants.value.id),
    companyId: Number(companyId.value),
    tax: Number(companyConstants.value.tax),
    addition: Number(companyConstants.value.addition),
  }
  if (!Number.isInteger(payload.id) || payload.id <= 0) {
    showToast('Company constants id is missing', 'error')
    return
  }
  if (!Number.isFinite(payload.tax) || payload.tax < 0 || !Number.isFinite(payload.addition) || payload.addition < 0) {
    showToast('Tax and addition must be valid non-negative numbers', 'error')
    return
  }

  savingCompanyConstants.value = true
  try {
    const res = await apiFetch('/settings/company-constants', {
      method: 'PUT',
      body: JSON.stringify(payload),
    })
    if (!res.ok) {
      const err = await res.json().catch(() => ({}))
      showToast(err.message || 'Failed to update company constants', 'error')
      return
    }
    const data = await res.json()
    companyConstants.value = {
      id: data?.id ?? null,
      companyId: data?.companyId ?? null,
      tax: Number(data?.tax ?? payload.tax),
      addition: Number(data?.addition ?? payload.addition),
    }
    const menuCount = Number(data?.menusRecalculated ?? 0)
    showToast(
      menuCount > 0
        ? `Company constants updated successfully`
        : 'Company constants updated successfully'
    )
  } catch (err) {
    console.error('Error updating company constants:', err)
    showToast('Failed to update company constants', 'error')
  } finally {
    savingCompanyConstants.value = false
  }
}

async function loadSettings() {
  if (!companyId.value && !branchId.value) return
  loading.value = true
  error.value = ''
  try {
    const res = await apiFetch('/settings')
    if (res.ok) {
      const data = await res.json()
      settings.value = {
        ...settings.value,
        ...data,
      }
    }
  } catch (e) {
    console.error(e)
    error.value = 'Failed to load settings'
  } finally {
    loading.value = false
  }
}

async function updateSettings(patch) {
  saving.value = true
  error.value = ''
  try {
    const res = await apiFetch('/settings', {
      method: 'PUT',
      body: JSON.stringify(patch),
    })
    if (!res.ok) throw new Error()
    const data = await res.json()
    settings.value = { ...settings.value, ...data }
    return true
  } catch (e) {
    error.value = 'Failed to update settings'
    return false
  } finally {
    saving.value = false
  }
}

function togglePrinter() {
  if (saving.value) return
  const next = settings.value.is_printer === 1 ? 0 : 1
  settings.value.is_printer = next
  updateSettings({ is_printer: next })
}

function toggleDepartmentMerged() {
  if (saving.value) return
  const next = settings.value.is_department_merged === 1 ? 0 : 1
  settings.value.is_department_merged = next
  updateSettings({ is_department_merged: next })
}

async function toggleSivByApproval() {
  if (saving.value) return
  const prev = settings.value.siv_by_approval
  const next = settings.value.siv_by_approval === 1 ? 0 : 1
  settings.value.siv_by_approval = next
  const ok = await updateSettings({ siv_by_approval: next })
  if (!ok) settings.value.siv_by_approval = prev
}

async function toggleAttendanceDevice() {
  if (saving.value) return
  const prev = settings.value.is_attendance_device
  const next = settings.value.is_attendance_device === 1 ? 0 : 1
  settings.value.is_attendance_device = next
  const ok = await updateSettings({ is_attendance_device: next })
  if (!ok) settings.value.is_attendance_device = prev
}

async function toggleTableMandatory() {
  if (saving.value) return
  const prev = settings.value.is_table_mandatory
  const next = settings.value.is_table_mandatory === 1 ? 0 : 1
  settings.value.is_table_mandatory = next
  const ok = await updateSettings({ is_table_mandatory: next })
  if (!ok) settings.value.is_table_mandatory = prev
}

async function toggleMultipleOrderConfirmation() {
  if (saving.value) return
  const prev = settings.value.allow_multiple_order_confirmation
  const next = prev === 1 ? 0 : 1
  settings.value.allow_multiple_order_confirmation = next
  const ok = await updateSettings({ allow_multiple_order_confirmation: next })
  if (!ok) settings.value.allow_multiple_order_confirmation = prev
}

onMounted(() => {
  loadSettings()
  loadPMs()
  loadDCs()
  loadCompanyConstants()
  loadPasswordResetUsers()
})
</script>

<style scoped>
.toggle-switch {
  position: relative;
  display: inline-block;
  width: 34px;
  height: 18px;
  cursor: pointer;
  flex-shrink: 0;
}
.toggle-switch input {
  opacity: 0;
  width: 0;
  height: 0;
}
.toggle-slider {
  position: absolute;
  inset: 0;
  background-color: #ef4444;
  border-radius: 18px;
  transition: 0.25s;
}
.toggle-slider::before {
  content: '';
  position: absolute;
  height: 12px;
  width: 12px;
  left: 3px;
  bottom: 3px;
  background-color: white;
  border-radius: 50%;
  transition: 0.25s;
}
.toggle-switch input:checked + .toggle-slider {
  background-color: #16a34a;
}
.toggle-switch input:checked + .toggle-slider::before {
  transform: translateX(16px);
}

/* Toast Transition */
.toast-enter-active,
.toast-leave-active {
  transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
}
.toast-enter-from,
.toast-leave-to {
  opacity: 0;
  transform: translateX(30px);
}
</style>
